import * as Preact from "preact"
import Objdict from "objdict"

import "@frog/source/views/dev/DevInspectorUI.less"
import App from "@frog/source/models/App.js"
import Unit from "@frog/source/models/Unit.js"
import Dev from "@frog/source/models/admin/Dev.js"
import Navigation from "@frog/source/models/Navigation.js"
import SceneReadout from "@frog/source/views/dev/SceneReadout.jsx"

import unitsprites from "@frog/source/data/unitsprites.js"
import unitJobs from "@frog/source/data/unit-jobs.js"

function readDivergenceIntoHTML(divergence, a, b) {
    return Object.keys(divergence).map((key) => {
        const value = divergence[key]
        if(value != undefined
        && typeof value != "string"
        && Object.keys(value).length > 0) {
            const subobjectHTML = readDivergenceIntoHTML(value, a[key] || {}, b[key] || {})
            if(subobjectHTML.filter((value) => value != null).length == 0) return null
            return (
                <div class="SubObject">
                    <div class="ObjectLabel">{key}</div>
                    {subobjectHTML}
                </div>
            )
        }
        if(divergence[key] == false) {
            return (
                <div class="DivergencePair">
                    <div class="Label">{key}</div>
                    <div class="EndstateValue">{JSON.stringify(a[key], null, 4) || "undefined"}</div>
                    <div class="EndstateValue">{JSON.stringify(b[key], null, 4) || "undefined"}</div>
                </div>
            )
        } else {
            return null
        }
    })
}

// === OMNI INSPECTION === //
export default class DevInspectorUI {
    render() {
        if(App.game?.chapter == undefined) return
        const selection = App.game.chapter.selection

        if(Dev.devSelection.selectedSavestateReport) {
            const inspectedObject = Dev.devSelection.selectedSavestateReport
            return (
                <div class="DevSavestateInspector">
                    {readDivergenceIntoHTML(inspectedObject.divergence, inspectedObject.endstateFromRecord, inspectedObject.endstateFromActions)}
                </div>
            )
        }
        if(Dev.mode == "geography") {
            if(Dev.devSelection.selectedEncounterKey != undefined
            || Dev.devSelection.hoveredEncounterKey != undefined) {
                if(Dev.devSelection.selectedVertexKey != undefined
                || Dev.devSelection.hoveredVertexKey != undefined) {
                    return <DevVertexInspectorUI/>
                }
                return <DevEncounterInspectorUI/>
            }
        } else if(Dev.mode == "scripts") {
            if(Dev.devSelection.selectedScene != undefined) {
                return <DevScriptInspectorUI/>
            }
        } else if(Dev.mode == "battle") {
            if(selection.hoveredUnitKey != undefined
            || selection.selectedUnitKey != undefined) {
                return <DevUnitInspectorUI/>
            } else if(Dev.devSelection.selectedSpotKey != undefined
                    || Dev.devSelection.hoveredSpotKey != undefined) {
                return <DevSpotInspectorUI/>
            }
        }
        return (
            <div class="DevInspector">
                Select an Object
            </div>
        )
    }
}

// === UNIT INSPECTION === //

const PROPERTY_HINTS = {
    "health": "The unit's remaining health in points.",
    "maxhealth": "The maximum number of health points for the unit",
    "key": "The unique id of the unit. It must be unique!!",
    "affiliation": "Which side the unit is on, be it good or bad or none.",
    "moverange": "How many tiles of movement the unit can take each turn.",
    "faceKey": "The face (or head) sprite, keyed from the spritesheet.",
    "bodyKey": "The body (or torso) sprite, keyed from the spritesheet.",
    "jobKey": "The job (or class) of the unit, keyed from a defined job list. Examples include sword or bow.",
    "randomJobKey": "A comma separated list of job keys to randomized between with each playthrough.",
}
const FIXED_ORDER_FOR_UNIT_PROPERTIES = [
    "affiliation",
    "jobKey",
    "health", "maxhealth",
    "position",
    "faceKey", "bodyKey",
    "ai",
    "properties",
    "key",
]
const SUGGESTED_VALUES = {
    "jobKey": Object.keys(unitJobs),
    "bodyKey": Object.keys(unitsprites.bodies),
    "faceKey": Object.keys(unitsprites.faces),
    "weaponImageKey": Object.keys(unitsprites.weapons),
    "affiliation": ["good", "bad", "none", "neutral"]
}

class DevUnitInspectorUI {
    render() {
        if(App.game == undefined) return
        if(Dev.isInspectorShowing() != true) return
        const selection = App.game.chapter?.selection
        const key = selection.selectedUnitKey || selection.hoveredUnitKey || Dev.devSelection.listHoveredUnitKey
        if(key == undefined) {
            const modedUnitsList = Dev.isEditingProto() ? Dev.loadedProtostage.units : App.game.chapter.stage.units
            return (
                <div class="DevInspector">
                    Select a Unit
                </div>
            )
        }
        let modedInspectedUnit
        let modedUnitList = []
        if(Dev.isEditingProto() == true) {
            modedUnitList = Dev.loadedProtostage.units
            modedInspectedUnit = Dev.loadedProtostage.units[key]
        } else {
            modedUnitList = App.game.chapter.stage.units
            modedInspectedUnit = App.game.chapter.stage.units[key]
        }
        if(modedInspectedUnit == undefined) {
            return (
                <div class="DevInspector">
                    Selected unit is not defined in this mode.
                    Select anew
                </div>
            )
        }
        const isSelected = (modedInspectedUnit.key == App.game.chapter.selection.selectedUnitKey)
        // Assemble a compound stand-in unit from the multiply selected units
        if(Dev.isEditingProto() == true
        && selection.multiselectedUnitKeys.size > 1) {
            const units = []
            let overlappingPropertiesKeys = Object.keys(Dev.loadedProtostage.units[key])
            selection.multiselectedUnitKeys.forEach((unitKey) => {
                let overlap = overlappingPropertiesKeys.filter(
                    (propertyKey) => {
                        return Object.keys(Dev.loadedProtostage.units[unitKey]).includes(propertyKey)
                    }
                )
                overlappingPropertiesKeys = overlap
                units.push(Dev.loadedProtostage.units[unitKey])
            })
            const compoundUnit = Objdict.map(Dev.loadedProtostage.units, (unit) => Unit.clean(Unit.copy(unit)))
            overlappingPropertiesKeys.forEach((propertyKey) => {
                selection.multiselectedUnitKeys.forEach((unitKey) => {
                    if(Dev.loadedProtostage.units[unitKey][propertyKey] != compoundUnit[propertyKey]) {
                        delete compoundUnit[propertyKey]
                    }
                })
            })
            compoundUnit.position = {
                "x": Math.min(...units.map((unit) => unit.position.x)) + " - " + Math.max(...units.map((unit) => unit.position.x)),
                "y": Math.min(...units.map((unit) => unit.position.y)) + " - " + Math.max(...units.map((unit) => unit.position.y)),
                "z": Math.min(...units.map((unit) => unit.position.z)) + " - " + Math.max(...units.map((unit) => unit.position.z)),
            }

            function setProperty(value, propertyKey) {
                if(value == "") {
                    selection.multiselectedUnitKeys.forEach((unitKey) => {
                        delete Dev.loadedProtostage.units[unitKey][propertyKey]
                    })
                } else {
                    selection.multiselectedUnitKeys.forEach((unitKey) => {
                        Dev.loadedProtostage.units[unitKey][propertyKey] = value
                    })
                }
            }
            return (
                <div class="DevUnitInspector" isSelected={isSelected}>
                    <div class="Header">
                        <div class="Name">{"Units"}</div>
                    </div>
                    <form>
                        <div>
                            {
                                Object.keys(compoundUnit.position).map((subkey) => {
                                    let propertyType = undefined
                                    if(isNaN(compoundUnit.position[subkey]) == false) propertyType = "number"
                                    return (
                                        <EditableSubproperty propertyKey={"position"}
                                            subpropertyKey={subkey} type={propertyType}
                                            onChangeCallback={(value) => {
                                                selection.multiselectedUnitKeys.forEach((unitKey) => {
                                                    Dev.loadedProtostage.units[unitKey].position[subkey] = value
                                                })
                                            }}
                                            label={"position" + "." + subkey} tooltip={PROPERTY_HINTS["position"]}
                                            modedUnit={compoundUnit}/>
                                    )
                                })
                            }
                        </div>
                        <NovelProperty
                            onChangeCallback={(value, propertyKey) => {setProperty(value, propertyKey)}}
                            modedUnit={compoundUnit}/>
                        {Object.keys(compoundUnit).sort().map((key) => {
                            let propertyType = undefined
                            if(typeof compoundUnit[key] == "string") propertyType = "text"
                            if(compoundUnit[key] == "") propertyType = "text"
                            if(compoundUnit[key] == null) propertyType = "text"
                            if(isNaN(compoundUnit[key]) == false) propertyType = "number"

                            if(key == "jobKey") {
                                return (
                                    <EditableProperty key={compoundUnit.key + key} propertyKey={key} type={propertyType}
                                        label={key} tooltip={PROPERTY_HINTS[key]}
                                        onChangeCallback={(newKey) => {
                                            setProperty(value, key)
                                            selection.multiselectedUnitKeys.forEach((unitKey) => {
                                                Dev.cacheUnitReferredProperties({"unit": Dev.loadedProtostage.units[unitKey]})
                                            })
                                        }}
                                        modedUnit={compoundUnit} modedList={modedUnitList}/>
                                )
                            }
                            if(propertyType != undefined) {
                                return (
                                    <EditableProperty key={compoundUnit.key + key} propertyKey={key} type={propertyType}
                                        label={key} tooltip={PROPERTY_HINTS[key]}
                                        onChangeCallback={(value) => {setProperty(value, key)}}
                                        modedUnit={compoundUnit}/>
                                )
                            }
                        })}
                    </form>
                </div>
            )
        }
        const unitPropertiesKeys = Object.keys(modedInspectedUnit)
        return (
            <div class="DevUnitInspector" isSelected={isSelected}>
                <div class="Header">
                    <div class="Name">{modedInspectedUnit.name || "Unit"}</div>
                    <div class="Key">(#{(modedInspectedUnit.key + "").split("-")[0]})</div>
                </div>
                <form>
                    {FIXED_ORDER_FOR_UNIT_PROPERTIES.map((key) => {
                        let propertyType = undefined
                        const unitProperty = modedInspectedUnit[key]
                        if(unitProperty == "") propertyType = "text"
                        if(unitProperty == null) propertyType = "text"
                        if(isNaN(unitProperty) == false) propertyType = "number"

                        if(key == "key") {
                            return (
                                <EditableProperty key={modedInspectedUnit.key + key} propertyKey={key} type={propertyType}
                                    label={key} tooltip={PROPERTY_HINTS[key]}
                                    onChangeCallback={(newKey) => {App.game.chapter.selection.selectedUnitKey = newKey; Dev.cacheUnitReferredProperties({"unit": modedInspectedUnit})}}
                                    modedUnit={modedInspectedUnit} modedList={modedUnitList}/>
                            )
                        }
                        if(key == "jobKey") {
                            return (
                                <EditableProperty key={modedInspectedUnit.key + key} propertyKey={key} type={propertyType}
                                    label={key} tooltip={PROPERTY_HINTS[key]}
                                    onChangeCallback={(newKey) => {Dev.cacheUnitReferredProperties({"unit": modedInspectedUnit})}}
                                    modedUnit={modedInspectedUnit} modedList={modedUnitList}/>
                            )
                        }
                        if(typeof unitProperty == "string"
                        || isNaN(unitProperty) == false
                        || unitProperty == null
                        || unitProperty == undefined) {
                            return (
                                <EditableProperty key={modedInspectedUnit.key + key} propertyKey={key} type={propertyType}
                                    label={key} tooltip={PROPERTY_HINTS[key]}
                                    modedUnit={modedInspectedUnit}/>
                            )
                        }
                        // if it's not a simple data type like string or number
                        // then it's probably got sub-properties that need listing
                        return (
                            <div>
                                {
                                    Object.keys(unitProperty).map((subkey) => {
                                        let propertyType = undefined
                                        if(isNaN(unitProperty[subkey]) == false) propertyType = "number"
                                        return (
                                            <EditableSubproperty propertyKey={key}
                                                subpropertyKey={subkey} type={propertyType}
                                                label={key + "." + subkey} tooltip={PROPERTY_HINTS[key]}
                                                modedUnit={modedInspectedUnit}/>
                                        )
                                    })
                                }
                            </div>
                        )
                    })}
                    <NovelProperty
                        modedUnit={modedInspectedUnit}/>
                    {unitPropertiesKeys.sort().map((key) => {
                        if(FIXED_ORDER_FOR_UNIT_PROPERTIES.includes(key)) return
                        let propertyType = undefined
                        if(typeof modedInspectedUnit[key] == "string") propertyType = "text"
                        if(modedInspectedUnit[key] == "") propertyType = "text"
                        if(modedInspectedUnit[key] == null) propertyType = "text"
                        if(isNaN(modedInspectedUnit[key]) == false) propertyType = "number"

                        if(propertyType != undefined) {
                            return (
                                <EditableProperty key={modedInspectedUnit.key + key} propertyKey={key} type={propertyType}
                                    label={key} tooltip={PROPERTY_HINTS[key]}
                                    modedUnit={modedInspectedUnit}/>
                            )
                        }
                    })}
                </form>
            </div>
        )
    }
}

// === SCRIPT INSPECTION === //

export class DevScriptInspectorUI {
    render() {
        if(Dev.loadedScript == undefined) {
            if(history.state == null
            || history.state.dataFolder == undefined) {
                return (
                    <div class="DevInspector">
                        <span onClick={() => Dev.readScriptFile()}>
                            Please load a script
                        </span>
                    </div>
                )
            } else {
                Dev.readScriptFile()
                return
            }
        }
        return (
            <div class="DevScriptInspector">
                <SceneReadout/>
            </div>
        )
    }
}

// === SPOT INSPECTION === //

class DevSpotInspectorUI {
    render() {
        if(App.game == undefined) return
        if(Dev.isInspectorShowing() != true) return
        if(Dev.devSelection.selectedSpotKey == undefined
        && Dev.devSelection.hoveredSpotKey == undefined) {
            return (
                <div class="DevInspector">
                    Select a Spot
                </div>
            )
        }
        const inspectedObject = Dev.loadedProtostage.world.spots[Dev.devSelection.selectedSpotKey || Dev.devSelection.hoveredSpotKey]
        if(inspectedObject == undefined) return
        return (
            <div class="DevSpotInspector" isSelected={Dev.devSelection.selectedSpotKey != undefined}>
                <div class="Header">
                    <div class="Name">{inspectedObject.tag || "Spot"}</div>
                </div>
                <form>
                    {Object.keys(inspectedObject).sort().map((propertyKey) => {
                        let propertyType = undefined
                        if(inspectedObject[propertyKey] == "") propertyType = "text"
                        if(inspectedObject[propertyKey] == null) propertyType = "text"
                        if(isNaN(inspectedObject[propertyKey]) == false) propertyType = "number"

                        if(propertyKey == "key") {
                            return (
                                <EditableProperty key={inspectedObject.key + propertyKey} propertyKey={propertyKey} type={propertyType}
                                    label={propertyKey}
                                    onChangeCallback={(newKey) => {Dev.devSelection.selectedSpotKey = newKey}}
                                    modedUnit={inspectedObject} modedList={Dev.loadedProtostage.world.spots}/>
                            )
                        }
                        if(typeof inspectedObject[propertyKey] == "string"
                        || isNaN(inspectedObject[propertyKey]) == false) {
                            return (
                                <EditableProperty key={inspectedObject.key + propertyKey} propertyKey={propertyKey} type={propertyType}
                                    label={propertyKey}
                                    modedUnit={inspectedObject}/>
                            )
                        }
                        // if it's not a simple data type like string or number
                        // then it's probably got sub-properties that need listing
                        return (
                            <div>
                                {
                                    Object.keys(inspectedObject[propertyKey]).map((subkey) => {
                                        let propertyType = undefined
                                        if(isNaN(inspectedObject[propertyKey][subkey]) == false) propertyType = "number"
                                        return (
                                            <EditableSubproperty key={inspectedObject.key + propertyKey + "." + subkey} propertyKey={propertyKey}
                                                subpropertyKey={subkey} type={propertyType}
                                                label={propertyKey + "." + subkey}
                                                modedUnit={inspectedObject}/>
                                        )
                                    })
                                }
                            </div>
                        )
                    })}
                </form>
            </div>
        )
    }
}

// === ENCOUNTER INSPECTION === //

class DevVertexInspectorUI {
    render() {
        if(App.game == undefined) return
        if(Dev.isInspectorShowing() != true) return
        const inspectedObject = Dev.loadedProtostage.world.zones[Dev.devSelection.selectedEncounterKey || Dev.devSelection.hoveredEncounterKey].points.find((vertex) => {
                return vertex.key == Dev.devSelection.selectedVertexKey
                    || vertex.key == Dev.devSelection.hoveredVertexKey
            })
        if(inspectedObject == undefined) {
            return (
                <div class="DevInspector">
                    Select a Vertex
                </div>
            )
        }
        return (
            <div class="DevVertexInspector" isSelected={Dev.devSelection.selectedVertexKey != undefined}>
                <div class="Header">
                    <div class="Name">{inspectedObject.key || "Vertex"}</div>
                </div>
                <form>
                    {Object.keys(inspectedObject).sort().map((key) => {
                        let propertyType = undefined
                        if(isNaN(inspectedObject[key]) == false) propertyType = "number"
                        if(inspectedObject[key] == null) propertyType = "text"
                        if(inspectedObject[key] == "") propertyType = "text"

                        if(typeof inspectedObject[key] == "string"
                        || isNaN(inspectedObject[key]) == false) {
                            return (
                                <EditableProperty key={inspectedObject.key + key} propertyKey={key} type={propertyType}
                                    label={key} tooltip={PROPERTY_HINTS[key]}
                                    modedUnit={inspectedObject}/>
                            )
                        }
                    })}
                </form>
            </div>
        )
    }
}
class DevEncounterInspectorUI {
    render() {
        if(App.game == undefined) return
        if(Dev.isInspectorShowing() != true) return
        if(Dev.devSelection.selectedEncounterKey == undefined
        && Dev.devSelection.hoveredEncounterKey == undefined) {
            return (
                <div class="DevInspector">
                    Select an Encounter
                </div>
            )
        }
        const inspectedEncounterKey = Dev.devSelection.selectedEncounterKey || Dev.devSelection.hoveredEncounterKey
        const inspectedObject = Dev.loadedProtostage.world.zones[inspectedEncounterKey]
        return (
            <div class="DevEncounterInspector" isSelected={Dev.devSelection.selectedEncounterKey != undefined}>
                <div class="Header">
                    <div class="Name">{inspectedObject.key || "Encounter"}</div>
                </div>
                <form>
                    {Object.keys(inspectedObject).sort().map((key) => {
                        let propertyType = undefined
                        if(inspectedObject[key] == null) propertyType = "text"
                        if(inspectedObject[key] == "") propertyType = "text"
                        if(isNaN(inspectedObject[key]) == false) propertyType = "number"

                        if(key == "key") {
                            return (
                                <EditableProperty key={inspectedObject.key + key}
                                    propertyKey={key} type={propertyType} label={key}
                                    onChangeCallback={(newKey) => {Dev.devSelection.selectedEncounterKey = newKey}}
                                    modedUnit={inspectedObject} modedList={Dev.loadedProtostage.world.zones}/>
                            )
                        }
                        if(typeof inspectedObject[key] == "string"
                        || isNaN(inspectedObject[key]) == false) {
                            return (
                                <EditableProperty key={inspectedObject.key + key}
                                    propertyKey={key} type={propertyType} label={key}
                                    modedUnit={inspectedObject}/>
                            )
                        }
                    })}
                </form>
            </div>
        )
    }
}

// === GENERAL UTILITIES === //

class EditableProperty {
    render() {
        if(this.list != undefined) {
            return (
                <label class="EditableProperty" key={this.props.key} isGrayedOut={this.isGrayedOut}>
                    <datalist id={this.props.label + "SuggestedValues"}>
                        {this.list.map((suggestion, suggestionkey) =>
                            <option key={suggestionkey} value={suggestion}>
                                {suggestion}
                            </option>
                        )}
                    </datalist>
                    <div class="Label" title={this.props.tooltip}>
                        <span class="Text">{this.props.label}</span>
                    </div>
                    <input class="Value" key={this.props.label} type={this.type}
                        defaultValue={this.defaultValue} list={this.props.label + "SuggestedValues"}
                        onChange={this.onChange} onKeyDown={this.onKeyDown}/>
                </label>
            )
        }
        return (
            <label class="EditableProperty" key={this.props.key}>
                <div class="Label" title={this.props.tooltip}>
                    <span class="Text">{this.props.label}</span>
                </div>
                <input class="Value" key={this.props.key + "_input"} type={this.type}
                    defaultValue={this.defaultValue} list={this.list}
                    onChange={this.onChange} onKeyDown={this.onKeyDown}/>
            </label>
        )
    }
    get list() {
        if(SUGGESTED_VALUES[this.props.propertyKey] != undefined) {
            return SUGGESTED_VALUES[this.props.propertyKey]
        }
        return undefined
    }
    get type() {
        if(this.props.type == "number") {
            return "number"
        }
        return "text"
    }
    get defaultValue() {
        const value = this.props.modedUnit[this.props.propertyKey]
        // if(value == undefined) return "undefined"
        // if(value == null) return "null"
        // if(value == "") return "empty-string"
        return value
    }
    get isGrayedOut() {
        return this.props.modedUnit[this.props.propertyKey] == undefined
            || this.props.modedUnit[this.props.propertyKey] == ""
            || this.props.modedUnit[this.props.propertyKey] == null
    }
    get onKeyDown() {
        return (event) => {
            event.stopPropagation()
        }
    }
    get onChange() {
        return (event) => {
            event.preventDefault()
            let changedInput = event.target.value
            if(Dev.isEditingProto() == true) {
                Dev.saveToUndo()
            }


            if(this.props.propertyKey == "key") {
                const oldKey = this.props.modedUnit.key
                this.props.modedList[changedInput] = this.props.modedUnit
                this.props.modedUnit[this.props.propertyKey] = changedInput
                delete this.props.modedList[oldKey]
            } else {
                if(this.props.type == "number") {
                    changedInput = parseInt(changedInput || 0)
                    if(isNaN(changedInput)) {
                        console.warn(event.target.value, "was expected to be a number, but is not a number.")
                    }
                }

                if(changedInput == "") {
                    delete this.props.modedUnit[this.props.propertyKey]
                } else {
                    this.props.modedUnit[this.props.propertyKey] = changedInput
                }
            }

            if(this.props.onChangeCallback != undefined) this.props.onChangeCallback(changedInput)
            App.game.chapter.recalculateStage()
            App.game.chapter.saveStage()
            Dev.autosaveUnitsToFile()
        }
    }
}

class EditableSubproperty extends EditableProperty {
    get defaultValue() {
        return this.props.modedUnit[this.props.propertyKey][this.props.subpropertyKey]
    }
    get onChange() {
        return (event) => {
            event.preventDefault()
            let changedInput = event.target.value
            if(Dev.isEditingProto() == true) {
                Dev.saveToUndo()
            }

            if(this.props.type == "number") {
                changedInput = parseInt(changedInput || 0)
                if(isNaN(changedInput)) {
                    console.warn(event.target.value, "was expected to be a number, but is not a number.")
                }
            }

            if(this.props.onChangeCallback != undefined) this.props.onChangeCallback(changedInput)
            if(this.props.modedUnit[this.props.propertyKey] != undefined) {
                if(changedInput == "") {
                    delete this.props.modedUnit[this.props.propertyKey]
                } else {
                    this.props.modedUnit[this.props.propertyKey][this.props.subpropertyKey] = changedInput
                }

                App.game.chapter.recalculateStage()
                App.game.chapter.saveStage()
                Dev.autosaveUnitsToFile()
            } else {
                console.log("Cannot write subproperty of a unit property that does not exist")
            }
        }
    }
}

class NovelProperty extends EditableProperty {
    render() {
        return (
            <label class="EditableProperty">
                <input class="Label" type="text"
                    onChange={this.onLabelChange} onKeyDown={this.onKeyDown}/>
                <input class="Value"
                    onChange={this.onChange} onKeyDown={this.onKeyDown}/>
            </label>
        )
    }
    get onChange() {
        return (event) => {
            event.preventDefault()

            this.newValue = event.target.value
            if(this.newLabel == undefined
            || this.newLabel === "") return

            if(Dev.isEditingProto() == true) {
                Dev.saveToUndo()
            }

            this.props.modedUnit[this.newLabel] = this.newValue || null

            if(this.props.onChangeCallback != undefined) this.props.onChangeCallback(changedInput, this.newLabel)
            App.game.chapter.recalculateStage()
            App.game.chapter.saveStage()
            Dev.autosaveUnitsToFile()
        }
    }
    get onLabelChange() {
        return (event) => {
            event.preventDefault()
            let changedLabel = event.target.value

            if(changedLabel === "") return
            this.newLabel = changedLabel
        }
    }
}
